/**
 * Reverse-lookup helpers: SIKB code → human-readable label.
 * All functions return null if the code is unknown (never throw).
 */

import {
  BODEMSOORT_HISTORISCH, TOEVOEGING, GRADATIE, MEDIAAN,
  KLEUR, KLEUR_STERKTE, BZB_TYPE, BZB_GRADATION, BZB_ORIGIN,
  MEETPUNT_TYPE, BOORSYSTEEM,
} from './index.js';

function reverse(map: Map<string, string>): Map<string, string> {
  const r = new Map<string, string>();
  for (const [label, code] of map) {
    if (!r.has(code)) r.set(code, label); // first entry wins for duplicates
  }
  return r;
}

// Historisch en niet BODEMSOORT: een label opzoeken is iets anders dan een keuze aanbieden.
// Keien is uit de keuzelijst gehaald, maar oude lagen dragen de code nog.
const _bodemsoort  = reverse(BODEMSOORT_HISTORISCH);
const _toevoeging  = reverse(TOEVOEGING);
const _gradatie    = reverse(GRADATIE);
const _mediaan     = reverse(MEDIAAN);
const _kleur       = reverse(KLEUR);
const _kleurSterkte = reverse(KLEUR_STERKTE);
const _bzbType     = reverse(BZB_TYPE);
const _bzbGradation = reverse(BZB_GRADATION);
const _bzbOrigin   = reverse(BZB_ORIGIN);
const _meetpuntType = reverse(MEETPUNT_TYPE);
const _boorsysteem = reverse(BOORSYSTEEM);

export const getSoilLabel       = (code?: string | null) => _bodemsoort.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getToevoegingLabel = (code?: string | null) => _toevoeging.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getGradatieLabel   = (code?: string | null) => _gradatie.get(code ?? '') ?? code ?? null;
export const getMediaanLabel    = (code?: string | null) => _mediaan.get(code ?? '') ?? code ?? null;
export const getKleurLabel      = (code?: string | null) => _kleur.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getKleurSterkte    = (code?: string | null) => _kleurSterkte.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getBzbTypeLabel    = (code?: string | null) => _bzbType.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getBzbGradationLabel = (code?: string | null) => _bzbGradation.get(code ?? '') ?? code ?? null;
export const getBzbOriginLabel  = (code?: string | null) => _bzbOrigin.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getMeetpuntTypeLabel = (code?: string | null) => _meetpuntType.get((code ?? '').toUpperCase()) ?? code ?? null;
export const getBoorsysteemLabel = (code?: string | null) => _boorsysteem.get((code ?? '').toUpperCase()) ?? code ?? null;

/** Soil main components for which median grain size (mediaan / Ø) is meaningful. */
export const MEDIAAN_SOIL_NAMES = ['zand', 'grind', 'slib'];

/**
 * Whether median grain size (mediaan) applies to a soil layer.
 * Accepts either a SIKB soil code (e.g. 'Z') or a soil name (e.g. 'zand').
 * Median is only meaningful for granular soils — for klei/leem/veen it is
 * ignored, so a stale value (e.g. left over after switching the soil type to
 * klei) must never be displayed or exported.
 */
export function mediaanApplies(soilCodeOrName?: string | null): boolean {
  if (!soilCodeOrName) return false;
  const name = (getSoilLabel(soilCodeOrName) ?? soilCodeOrName).toLowerCase().trim();
  return MEDIAAN_SOIL_NAMES.includes(name);
}

/**
 * Suggested depth range (cm) for a new jar (potje). Jars are 50 cm tall and the
 * "from" snaps to a 50 cm grid (0, 50, 100, …). The first jar is 0–50; otherwise
 * we take the deepest existing jar's "to", round it to the nearest 50 cm grid
 * value and start there (e.g. existing jars ending at 110 → next jar 100–150).
 * Pre-filling something that is right ~80% of the time and stays editable beats
 * leaving the fields blank.
 */
export function nextJarDepth(existingTos: Array<number | null | undefined>): { from: number; to: number } {
  const tos = (existingTos ?? []).map(Number).filter(v => Number.isFinite(v));
  if (!tos.length) return { from: 0, to: 50 };
  const from = Math.round(Math.max(...tos) / 50) * 50;
  return { from, to: from + 50 };
}

/** Format a colour object { ti_color_main, ti_color_shade, ti_color_sub } to human string. */
export function formatKleur(kleur: { ti_color_main?: string | null; ti_color_shade?: string | null; ti_color_sub?: string | null } | null | undefined): string | null {
  if (!kleur) return null;
  const parts = [
    getKleurSterkte(kleur.ti_color_shade),
    getKleurLabel(kleur.ti_color_main),
    getKleurLabel(kleur.ti_color_sub),
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
}

/** Format a BZB item to human string. */
export function formatBzb(bzb: { ti_bzb_type?: string | null; type_naam?: string | null; ti_bzb_gradation?: string | null; ti_bzb_origin?: string | null }): string {
  const type = bzb.type_naam || getBzbTypeLabel(bzb.ti_bzb_type) || bzb.ti_bzb_type || '';
  const grad = getBzbGradationLabel(bzb.ti_bzb_gradation);
  const orig = getBzbOriginLabel(bzb.ti_bzb_origin);
  const base = [grad, type].filter(Boolean).join(' ');
  return orig ? `${base} (${orig})` : base;
}

/**
 * Robustly extract a string value from nested JSON objects.
 * Handles plain strings/numbers, { waarde }, { ti_color_main }, { ti_bzb_type }, arrays.
 */
export function extractText(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'number') return String(val);
  if (Array.isArray(val)) return val.map(extractText).filter(Boolean).join(', ');
  if (typeof val === 'object') {
    const o = val as Record<string, unknown>;
    if (o['ti_bzb_type']) return formatBzb(o as Parameters<typeof formatBzb>[0]) || '';
    if (o['ti_color_main']) return formatKleur(o as Parameters<typeof formatKleur>[0]) || '';
    for (const k of ['waarde', 'type_naam', 'omschrijving', 'identificatie', 'code', 'naam']) {
      if (o[k] !== undefined && o[k] !== null) return extractText(o[k]);
    }
  }
  return String(val);
}
